import React, { useEffect, useState } from 'react';
import { ArrowBendUpRight, ArrowSquareOut, CircleNotch, DownloadSimple, Envelope, EnvelopeSimple, PaperPlaneTilt, PencilSimple, PenNib, Sparkle, X } from '@phosphor-icons/react';
import { formatiereGroesse, istGoogleFormat, ladeDateiBlob, type DriveEintrag } from '../../../../lib/dokumente/aktenschrankApi';
import { driveDateiUrl } from '../../../../lib/dokumente/ablageApi';
import { quelleLabel } from '../../../../lib/dokumente/aktenschrank';
import type { DateiMeta } from '../../../../lib/dokumente/useAktenschrank';
import { dateiDarstellung, deDatumZeit, vorschauArt } from './dateiDarstellung';
import { FreigabenKarte } from '../../../../components/dokumente/FreigabenKarte';

const AKTION: React.CSSProperties = {
  minHeight: 44, padding: '0 12px', borderRadius: 14, border: 'none', gap: 7, fontSize: 13, fontWeight: 700,
  background: 'var(--fill2)', color: 'var(--ink)', textDecoration: 'none',
};

/**
 * Vorschau-Panel einer Datei (Handoff Dokumente §6.2, Screen 06): Bild-/PDF-Vorschau, Metadaten,
 * KI-Zusammenfassung (dokumente.ai_summary) und Aktionen. Bytes werden erst geladen, wenn das Panel offen ist.
 */
export function VorschauPanel({ datei, meta, ordnerName, onSchliessen, onHerunterladen, onUmbenennen, onVerschieben, onEmail, onSignatur, signaturLaeuft = false, onAnKollegen, onPost, onSchreiben }: {
  datei: DriveEintrag;
  meta: DateiMeta | null;
  ordnerName: string;
  onSchliessen: () => void;
  onHerunterladen: () => void;
  onUmbenennen: () => void;
  onVerschieben: () => void;
  onEmail: () => void;
  /** Nur bei PDFs: neuen Signatur-Entwurf aus der Datei anlegen. */
  onSignatur?: () => void;
  signaturLaeuft?: boolean;
  /** #549: als Verweis in den Team-Chat schicken */
  onAnKollegen?: () => void;
  /** E-POST: Datei als Brief verschicken (nur PDF/Bild). */
  onPost?: () => void;
  /** #586: Datei als Anlage in ein neues Schreiben (E-Mail und/oder Post) */
  onSchreiben?: () => void;
}) {
  const d = dateiDarstellung(datei.name, datei.mimeType);
  const art = istGoogleFormat(datei.mimeType) ? null : vorschauArt(datei.mimeType);
  const [url, setUrl] = useState<string | null>(null);
  const [laedt, setLaedt] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  useEffect(() => {
    setUrl(null); setFehler(null);
    if (!art) return;
    let aktiv = true;
    let objUrl: string | null = null;
    setLaedt(true);
    ladeDateiBlob(datei.id, datei.mimeType)
      .then(b => { if (!aktiv) return; objUrl = URL.createObjectURL(b); setUrl(objUrl); })
      .catch((e: unknown) => { if (aktiv) setFehler(e instanceof Error ? e.message : 'Vorschau fehlgeschlagen'); })
      .finally(() => { if (aktiv) setLaedt(false); });
    return () => { aktiv = false; if (objUrl) URL.revokeObjectURL(objUrl); };
  }, [datei.id, datei.mimeType, art]);

  const metaZeilen: [string, string][] = [
    ['Ordner', ordnerName],
    ['Größe', formatiereGroesse(datei.size) || '–'],
    ['Geändert', deDatumZeit(datei.modifiedTime) || '–'],
    ['Quelle', meta ? quelleLabel(meta.quelle, meta.kategorie) : 'Google Drive'],
    ['Kategorie', meta?.kategorie || '–'],
  ];
  const google = istGoogleFormat(datei.mimeType);

  return (
    <aside aria-label={`Vorschau ${datei.name}`} className="immo-glas immo-side-in flex flex-col min-w-0" style={{ borderRadius: 26, padding: 14, gap: 12 }}>
      <div className="flex items-start" style={{ gap: 10 }}>
        <span className="flex items-center justify-center shrink-0" style={{ width: 40, height: 40, borderRadius: 13, background: d.bg }}>
          <d.icon weight="fill" size={20} color={d.fg} />
        </span>
        <span className="flex-1 min-w-0" style={{ fontSize: 15, fontWeight: 800, lineHeight: 1.3, overflowWrap: 'anywhere' }}>{datei.name}</span>
        <button type="button" aria-label="Vorschau schließen" onClick={onSchliessen} className="flex items-center justify-center cursor-pointer shrink-0"
          style={{ width: 36, height: 36, borderRadius: '50%', border: 'none', background: 'var(--fill2)', color: 'var(--ink)' }}>
          <X weight="bold" size={13} />
        </button>
      </div>

      <div className="flex items-center justify-center overflow-hidden" style={{ borderRadius: 18, background: 'var(--fill2)', minHeight: art ? 260 : 120 }}>
        {laedt && <CircleNotch size={22} color="var(--ink3)" className="animate-spin" aria-label="Vorschau lädt" />}
        {fehler && <span style={{ padding: 16, fontSize: 12, color: 'var(--bad)', textAlign: 'center' }}>Vorschau fehlgeschlagen: {fehler}</span>}
        {url && art === 'bild' && <img src={url} alt={datei.name} style={{ maxWidth: '100%', maxHeight: 420, objectFit: 'contain' }} />}
        {/* Kein sandbox: Chromes PDF-Viewer rendert im sandboxed Iframe nicht. Sicher, weil der Blob-Typ hart auf application/pdf steht. */}
        {url && art === 'pdf' && <iframe src={url} title={datei.name} referrerPolicy="no-referrer" style={{ width: '100%', height: 460, border: 'none', background: '#fff' }} />}
        {!art && (
          <span className="flex flex-col items-center" style={{ gap: 6, padding: 16, fontSize: 12, color: 'var(--ink3)', textAlign: 'center' }}>
            <d.icon weight="duotone" size={34} color={d.fg} />
            {google ? 'Google-Dokument – in Drive öffnen' : 'Keine Vorschau für diesen Dateityp'}
          </span>
        )}
      </div>

      <dl className="m-0 grid" style={{ gridTemplateColumns: 'auto minmax(0,1fr)', columnGap: 12, rowGap: 6, fontSize: 12 }}>
        {metaZeilen.map(([k, v]) => (
          <React.Fragment key={k}>
            <dt style={{ color: 'var(--ink3)', fontWeight: 600 }}>{k}</dt>
            <dd className="m-0 truncate" style={{ fontWeight: 700 }} title={v}>{v}</dd>
          </React.Fragment>
        ))}
      </dl>

      {meta?.id && <FreigabenKarte dokumentId={meta.id} />}
      {meta?.ai_summary && (
        <div className="flex flex-col" style={{ borderRadius: 16, padding: '12px 14px', gap: 6, background: 'var(--acc-soft)' }}>
          <span className="flex items-center" style={{ gap: 6, fontSize: 11, fontWeight: 800, color: 'var(--acc)' }}><Sparkle weight="fill" size={13} />KI-Zusammenfassung</span>
          <span style={{ fontSize: 13, lineHeight: 1.5 }}>{meta.ai_summary}</span>
        </div>
      )}

      <div className="grid" style={{ gridTemplateColumns: 'repeat(2, minmax(0,1fr))', gap: 6 }}>
        {!google && (
          <button type="button" onClick={onHerunterladen} className="flex items-center justify-center cursor-pointer" style={{ ...AKTION, background: 'var(--acc-fill)', color: 'var(--acc-ink)', boxShadow: 'var(--acc-sh)' }}>
            <DownloadSimple weight="bold" size={15} />Herunterladen
          </button>
        )}
        <a href={driveDateiUrl(datei.id)} target="_blank" rel="noreferrer" className="immo-hover-flaeche flex items-center justify-center" style={AKTION}>
          <ArrowSquareOut weight="bold" size={15} />In Drive öffnen
        </a>
        {!google && (
          <button type="button" onClick={onEmail} className="immo-hover-flaeche flex items-center justify-center cursor-pointer" style={AKTION}>
            <EnvelopeSimple weight="bold" size={15} />Per E-Mail
          </button>
        )}
        {onSignatur && art === 'pdf' && (
          <button type="button" onClick={onSignatur} disabled={signaturLaeuft} className="immo-hover-flaeche flex items-center justify-center cursor-pointer" style={{ ...AKTION, opacity: signaturLaeuft ? 0.6 : 1 }}>
            {signaturLaeuft ? <CircleNotch size={15} className="animate-spin" /> : <PenNib weight="bold" size={15} />}Zur Signatur
          </button>
        )}
        {onPost && (art === 'pdf' || /png|jpe?g/i.test(datei.mimeType)) && (
          <button type="button" onClick={onPost} className="immo-hover-flaeche flex items-center justify-center cursor-pointer" style={AKTION} data-tip="Per Post – die Post druckt und verschickt die Datei als Brief">
            <Envelope weight="bold" size={15} />Per Post
          </button>
        )}
        {onSchreiben && (art === 'pdf' || /png|jpe?g/i.test(datei.mimeType)) && (
          <button type="button" onClick={onSchreiben} className="immo-hover-flaeche flex items-center justify-center cursor-pointer" style={AKTION} data-tip="Neues Schreiben – Datei als Anlage, per E-Mail und/oder Post">
            <PaperPlaneTilt weight="bold" size={15} />Neues Schreiben
          </button>
        )}
        {onAnKollegen && !google && (
          <button type="button" onClick={onAnKollegen} className="immo-hover-flaeche flex items-center justify-center cursor-pointer" style={AKTION} data-tip="An Kollegen – als Karte in den Team-Chat schicken (keine Kopie)">
            <PaperPlaneTilt weight="bold" size={15} />An Kollegen
          </button>
        )}
        <button type="button" onClick={onUmbenennen} className="immo-hover-flaeche flex items-center justify-center cursor-pointer" style={AKTION}>
          <PencilSimple weight="bold" size={15} />Umbenennen
        </button>
        <button type="button" onClick={onVerschieben} className="immo-hover-flaeche flex items-center justify-center cursor-pointer" style={AKTION}>
          <ArrowBendUpRight weight="bold" size={15} />Verschieben
        </button>
      </div>
    </aside>
  );
}
